/** Pure derivations for the delivery calendar (unit tested). Everything is computed from /v1/calendar/items rows; nothing is invented. */
import type { CalItem } from '../home/home-data';

export const STATUS_BUCKETS = ['draft', 'internal', 'customer', 'scheduled', 'published'] as const;
export type StatusBucket = (typeof STATUS_BUCKETS)[number];

/** Deliverable, social post and email states mapped onto the five delivery stages. Unknown or absent statuses have no stage (null). */
const BUCKET: Record<string, StatusBucket> = {
  draft: 'draft', changes_requested: 'draft', rejected: 'draft', failed: 'draft',
  internal_review: 'internal',
  client_review: 'customer', awaiting_approval: 'customer', pending_approval: 'customer', blocked_unapproved: 'customer',
  approved: 'scheduled', scheduled: 'scheduled', publishing: 'scheduled', retrying: 'scheduled', sending: 'scheduled',
  published: 'published', sent: 'published',
};
export const bucketOf = (item: Pick<CalItem, 'status'>): StatusBucket | null => BUCKET[item.status ?? ''] ?? null;

/** Item types shown as the "Channel" filter (what kind of work the item is). */
export const CHANNEL_TYPES = ['deliverable', 'approval', 'post', 'email_campaign', 'campaign', 'meeting', 'milestone'] as const;

export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
/** Monday on or before a YYYY-MM-DD date. */
export function mondayOf(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return addDays(date, -((dow + 6) % 7));
}
export const weekDates = (start: string): string[] => Array.from({ length: 7 }, (_, i) => addDays(start, i));
/** A UTC range wide enough for any time zone; items are bucketed by their own local date afterwards. */
export const queryRange = (start: string) => ({ from: `${addDays(start, -1)}T00:00:00Z`, to: `${addDays(start, 8)}T00:00:00Z` });

/** A calendar row; `ownerId`/`ownerLabel` are used when the items API returns them (today only the per-deliverable call has an owner). */
export type OwnedItem = CalItem & { ownerId?: string | null; ownerLabel?: string | null };
export interface CalFilters { type: string; status: string; owner?: string }
export const NO_FILTERS: CalFilters = { type: '', status: '', owner: '' };
export function filterItems<T extends OwnedItem>(items: readonly T[], f: CalFilters): T[] {
  return items.filter((i) => (!f.type || i.type === f.type) && (!f.status || bucketOf(i) === f.status) && (!f.owner || i.ownerId === f.owner));
}
/** Channel options: only item types present in the loaded week, in the canonical order, then any unknown ones. */
export const channelOptions = (items: readonly CalItem[]): string[] => {
  const present = new Set(items.map((i) => i.type));
  return [...CHANNEL_TYPES.filter((k) => present.has(k)), ...[...present].filter((k) => !(CHANNEL_TYPES as readonly string[]).includes(k)).sort()];
};
/** Owner options come only from items that carry an owner; none means the filter is unavailable. */
export function ownerOptions(items: readonly OwnedItem[]): { id: string; label: string }[] {
  const m = new Map<string, string>();
  for (const i of items) if (i.ownerId && !m.has(i.ownerId)) m.set(i.ownerId, i.ownerLabel || i.ownerId);
  return [...m].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label));
}
/** Fills ownerLabel from the team directory (/v1/team/members) when the API only returned ownerId; ids with no member keep no label. */
export function withOwnerLabels<T extends OwnedItem>(items: readonly T[], members: readonly { id: string; userId?: string; email?: string; displayName?: string | null; name?: string | null }[]): T[] {
  const label = (id: string) => { const m = members.find((x) => x.userId === id || x.id === id); return m?.displayName || m?.name || m?.email || null; };
  return items.map((i) => (i.ownerId && !i.ownerLabel ? { ...i, ownerLabel: label(i.ownerId) } : i));
}
/** Items of the visible week grouped by local date, each day in time order. */
export function groupByDay(items: readonly CalItem[], dates: readonly string[]): { date: string; items: CalItem[] }[] {
  return dates.map((date) => ({ date, items: items.filter((i) => i.local.date === date).sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt) || a.id.localeCompare(b.id)) }));
}
/** The next piece of work still to be delivered: first unfinished item at or after now, else the earliest overdue one. */
export function nextDue(items: readonly CalItem[], now: Date): CalItem | null {
  const open = items.filter((i) => bucketOf(i) !== 'published').sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  return open.find((i) => Date.parse(i.startsAt) >= now.getTime()) ?? open.find((i) => i.overdue) ?? null;
}

export type StepState = 'done' | 'current' | 'todo';
/** Approval chain: every stage before the item's stage is done, its own stage is current. Items without a stage have no chain. */
export function chainSteps(bucket: StatusBucket | null): { key: StatusBucket; state: StepState }[] {
  if (!bucket) return [];
  const at = STATUS_BUCKETS.indexOf(bucket);
  return STATUS_BUCKETS.map((key, i) => ({ key, state: bucket === 'published' ? 'done' : i < at ? 'done' : i === at ? 'current' : 'todo' }));
}
/** Only a deliverable still being prepared can be sent to the customer for approval (the API move to client_review). */
export const canRequestApproval = (i: Pick<CalItem, 'type' | 'status'>): boolean => i.type === 'deliverable' && (i.status === 'draft' || i.status === 'internal_review');
/** Id of the deliverable behind a calendar item (ids look like "deliverable:dlv_1"). */
export const deliverableId = (i: Pick<CalItem, 'id' | 'type'>): string | null => (i.type === 'deliverable' || i.type === 'approval' ? i.id.slice(i.id.indexOf(':') + 1) : null);
